import type { Skill } from "../lib/types";
import { navigateToSkill } from "../router";
import { scoreClass, tierClass, statusClass, TIER_LABEL } from "../lib/scoring";
import { formatDate, truncate } from "../lib/format";

interface Props {
  skill: Skill;
  compact?: boolean;
}

export function SkillCard({ skill, compact }: Props) {
  const sc = scoreClass(skill.combinedPercentage);
  const tc = tierClass(skill.tier);
  const st = statusClass(skill.status);

  if (compact) {
    return (
      <button
        type="button"
        onClick={() => navigateToSkill(skill.name)}
        className="w-full text-left flex items-center gap-3 px-3 py-2 border-b border-rule hover:bg-hover group"
      >
        <span className={`font-mono font-bold text-t-18 tabular-nums w-10 shrink-0 text-${sc}`}>
          {skill.combinedPercentage}
        </span>
        <span className="flex-1 min-w-0">
          <span className="font-mono text-t-12 uppercase font-medium truncate block group-hover:text-accent">
            {skill.name}
          </span>
          <span className="font-mono text-t-10 text-muted">
            {skill.lineCount} lines · {TIER_LABEL[skill.tier]}
          </span>
        </span>
        <span className={`font-mono text-t-10 uppercase tracking-wider shrink-0 text-${st}`}>
          {skill.status}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => navigateToSkill(skill.name)}
      className="w-full text-left border-2 border-fg bg-bg p-5 hover-shift hover:bg-hover group"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <h3 className="font-mono text-t-13 uppercase font-bold truncate group-hover:text-accent leading-snug">
          {skill.name}
        </h3>
        <span className={`font-mono text-t-10 uppercase px-2 py-0.5 border border-fg shrink-0 text-${st}`}>
          {skill.status}
        </span>
      </div>
      <p className="font-sans text-t-13 leading-relaxed text-fg/70 mb-3">
        {truncate(skill.description, 120)}
      </p>
      <div className="flex items-end justify-between gap-3">
        <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-t-10 text-muted uppercase tracking-wide">
          <span className={`text-${tc}`}>{TIER_LABEL[skill.tier]}</span>
          <span>{skill.lineCount} lines</span>
          <span>{formatDate(skill.lastScored)}</span>
        </div>
        <span className={`font-display font-extrabold text-t-32 leading-none tabular-nums text-${sc}`}>
          {skill.combinedPercentage}
        </span>
      </div>
    </button>
  );
}
